import React from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { Redirect } from '@atlassian/react-resource-router';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { ReportCategory } from '../report-category/index.tsx';
import type { reportCategories_reportsOverviewPage$key } from './__generated__/reportCategories_reportsOverviewPage.graphql';
import messages from './messages.tsx';

type OverviewPageProps = {
	reportCategoriesData: reportCategories_reportsOverviewPage$key;
};

export const ReportCategories = ({
	reportCategoriesData,
}: OverviewPageProps): React.JSX.Element => {
	const { showFlag } = useFlagsService();
	const { formatMessage } = useIntl();

	const data = useFragment<reportCategories_reportsOverviewPage$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment reportCategories_reportsOverviewPage on Query {
				jira_projectByIdOrKey(cloudId: $cloudId, idOrKey: $projectKey)
					@skip(if: $isBoardContext)
					@required(action: THROW) {
					reportCategories @optIn(to: "JiraReportCategories") @required(action: THROW) {
						errors {
							extensions @required(action: THROW) {
								statusCode @required(action: THROW)
							}
						}
						edges {
							node {
								name @required(action: THROW)
								...reportCategory_reportsOverviewPage
							}
						}
					}
				}
				jira @include(if: $isBoardContext) @required(action: THROW) {
					jiraBoard(id: $boardId) @required(action: THROW) {
						... on JiraBoard {
							reportCategories @optIn(to: "JiraReportCategories") @required(action: THROW) {
								edges {
									node @required(action: THROW) {
										name @required(action: THROW)
										...reportCategory_reportsOverviewPage
									}
								}
							}
						}
					}
				}
			}
		`,
		reportCategoriesData,
	);

	let categories;

	const projectQueryErrors = data.jira_projectByIdOrKey?.reportCategories?.errors ?? [];

	if (projectQueryErrors.length > 0) {
		const worstStatusCode = projectQueryErrors
			.flatMap((errors) => errors.extensions)
			.map((extension) => extension?.statusCode ?? 0)
			.reduce((prev, curr) => {
				return Math.max(prev, curr);
			}, 0);

		if (worstStatusCode >= 500) {
			throw new Error('5xx error returned for reports overview project query');
		}

		if (worstStatusCode >= 400) {
			showFlag({
				id: 'reportCategoriesOverviewQueryError',
				type: 'error',
				title: formatMessage(messages.projectNotFoundErrorFlagTitle),
				description: formatMessage(messages.projectNotFoundErrorFlagMessage),
				messageId:
					'reports-overview-page.ui.report-categories.show-flag.error.report-categories-overview-query-error.show-flag.error.1',
				messageType: 'transactional',
			});
			return <Redirect to="/jira/your-work" />;
		}
	}

	if (data.jira_projectByIdOrKey?.reportCategories?.edges) {
		categories = data.jira_projectByIdOrKey.reportCategories.edges.flatMap((categoryEdge) =>
			categoryEdge?.node ? [categoryEdge.node] : [],
		);
	} else if (data.jira?.jiraBoard.reportCategories?.edges) {
		categories = data.jira.jiraBoard.reportCategories?.edges.flatMap((categoryEdge) =>
			categoryEdge?.node ? [categoryEdge.node] : [],
		);
	}

	if (!categories) {
		throw new Error('reportCategoriesQuery returned no data');
	}

	const shouldShowHeading = categories && categories.length > 1;

	return (
		<>
			{categories.map((category) => (
				<JSErrorBoundary
					key={category.name}
					id="reportsOverviewPageReportCategory"
					packageName="jiraReportsOverviewPage"
					teamName="helios"
					fallback="unmount"
				>
					<ReportCategory category={category} shouldShowHeading={shouldShowHeading} />
				</JSErrorBoundary>
			))}
		</>
	);
};
